import { useMemo } from "react";
import { motion } from "framer-motion";
import { TrendingUp, Percent, Crown, Wallet, Repeat, Gauge, Trophy, Coins, Sparkles, Banknote, Boxes, Landmark, Gem } from "lucide-react";
import dayjs from "dayjs";
import { SectionHeader } from "./SectionHeader";
import { inrCompact, inrFull, prettyLoc } from "@/lib/format";

const EASE = [0.16, 1, 0.3, 1];

const Delta = ({ v }) =>
  v == null ? (
    <span className="text-[10px] text-slate-300 font-semibold">— YoY</span>
  ) : (
    <span
      className={`inline-flex items-center gap-1 text-[11px] font-bold tabular-nums ${
        v >= 0 ? "text-emerald-600" : "text-red-500"
      }`}
      data-testid="delta-yoy"
    >
      {v >= 0 ? "▲" : "▼"} {Math.abs(v).toFixed(1)}%
      <span className="text-slate-400 font-semibold">YoY</span>
    </span>
  );

const Card = ({ icon: Icon, label, value, sub, delta, i, testId }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.7, delay: i * 0.07, ease: EASE }}
    data-testid={testId}
    className="bg-white border border-slate-200 p-5 sm:p-6 shadow-sm hover:-translate-y-1 hover:shadow-md transition-transform h-full"
  >
    <div className="flex items-center justify-between mb-5">
      <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400">{label}</p>
      <Icon size={16} className="text-[#B08D26]" />
    </div>
    <p className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 tabular-nums">{value}</p>
    {sub && <p className="text-xs text-slate-500 mt-1.5 tabular-nums">{sub}</p>}
    <div className="flex items-center gap-4 mt-3">
      <Delta v={delta?.yoy} />
    </div>
  </motion.div>
);

export const OverviewSection = ({ data, single = false }) => {
  const k = data?.kpis;
  const d = data?.deltas;
  const grand = k ? k.excl_coins + k.coins : 0;

  const insights = useMemo(() => {
    if (!data) return [];
    const out = [];
    const stats = data.location_stats || [];
    const total = stats.reduce((s, l) => s + l.turnover, 0);
    if (stats.length >= 2 && total > 0) {
      const lead = stats.reduce((a, b) => (b.turnover > a.turnover ? b : a));
      out.push(`${prettyLoc(lead.location)} drives ${((lead.turnover / total) * 100).toFixed(1)}% of turnover`);
    }
    if (data.kpis?.excl_coins) {
      out.push(
        `GP margin ${((data.kpis.gp / data.kpis.excl_coins) * 100).toFixed(1)}% · after royalty ${((data.kpis.gp_after_royalty / data.kpis.excl_coins) * 100).toFixed(1)}%`
      );
    }
    if (stats.length >= 2) {
      const sorted = [...stats].sort((a, b) => (b.avg_daily || 0) - (a.avg_daily || 0));
      out.push(`Daily run-rate ${sorted.map((s) => `${prettyLoc(s.location)} ${inrCompact(s.avg_daily)}`).join(" vs ")}`);
    }
    for (const s of stats) {
      if (s.stock_turn != null) {
        out.push(`${prettyLoc(s.location)} stock-turn ${s.stock_turn.toFixed(2)}x annualised`);
      }
    }
    const c = data.categories;
    if (c) {
      const ct = c.gold + c.diamond + c.precious + c.silver;
      if (ct > 0) out.push(`Gold ${((c.gold / ct) * 100).toFixed(1)}% of merchandise mix`);
    }
    const best = (data.monthly || []).reduce((a, b) => (b.turnover > (a?.turnover || 0) ? b : a), null);
    if (best) out.push(`Best month ${best.label} · ${inrCompact(best.turnover)}`);
    if (data.deltas?.turnover?.yoy != null)
      out.push(`Turnover ${data.deltas.turnover.yoy >= 0 ? "▲" : "▼"} ${Math.abs(data.deltas.turnover.yoy).toFixed(1)}% YoY`);
    return out;
  }, [data]);

  return (
    <section id="overview" data-testid="kpi-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-14">
      <SectionHeader number="01" title="Overview" subtitle="Consolidated performance.">
        {d?.ref_label && (
          <p className="text-[11px] text-slate-400 font-semibold" data-testid="delta-ref">
            YoY vs {d.ref_label}
          </p>
        )}
      </SectionHeader>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card i={0} testId="kpi-turnover" icon={TrendingUp} label="Total Turnover" value={k ? inrCompact(k.turnover) : "—"} sub={k ? inrFull(k.turnover) : ""} delta={d?.turnover} />
        <Card i={1} testId="kpi-excl-coins" icon={Banknote} label="Turnover excl. Coins" value={k ? inrCompact(k.excl_coins) : "—"} sub={grand ? `${((k.excl_coins / grand) * 100).toFixed(1)}% of turnover` : ""} delta={d?.excl_coins} />
        <Card i={2} testId="kpi-coins" icon={Coins} label="Coin Sales" value={k ? inrCompact(k.coins) : "—"} sub={grand ? `${((k.coins / grand) * 100).toFixed(1)}% of turnover` : ""} delta={d?.coins} />
        <Card i={3} testId="kpi-gp" icon={Percent} label="Gross Profit" value={k ? inrCompact(k.gp) : "—"} sub={k?.excl_coins ? `${((k.gp / k.excl_coins) * 100).toFixed(1)}% margin` : ""} delta={d?.gp} />
        <Card i={4} testId="kpi-royalty" icon={Crown} label="Royalty" value={k ? inrCompact(k.royalty) : "—"} sub={k ? inrFull(k.royalty) : ""} delta={d?.royalty} />
        <Card i={5} testId="kpi-gp-after-royalty" icon={Wallet} label="GP after Royalty" value={k ? inrCompact(k.gp_after_royalty) : "—"} sub={k?.excl_coins ? `${((k.gp_after_royalty / k.excl_coins) * 100).toFixed(1)}% margin` : ""} delta={d?.gp_after_royalty} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200 mt-6" data-testid="kpi-mini-strip">
        {[
          {
            icon: Repeat,
            label: "Stock Turn",
            value: k?.stock_turn != null ? `${k.stock_turn.toFixed(2)}x` : "—",
            sub: "annualised · avg daily × 365 ÷ stock",
          },
          { icon: Gauge, label: "Avg Daily Sale", value: k ? inrCompact(k.avg_daily) : "—" },
          {
            icon: Trophy,
            label: "Best Day",
            value: k?.best_day ? inrCompact(k.best_day.value) : "—",
            sub: k?.best_day ? `${dayjs(k.best_day.date).format("DD MMM YYYY")}${single ? "" : ` · ${prettyLoc(k.best_day.location)}`}` : "",
          },
          { icon: Gem, label: "Studded Avg", value: k ? `${(k.studded_avg || 0).toFixed(1)}%` : "—" },
          {
            icon: Boxes,
            label: "Closing Stock",
            value: k?.closing_stock ? inrCompact(k.closing_stock.value) : "—",
            sub: k?.closing_stock ? `as of ${dayjs(k.closing_stock.date).format("DD MMM YYYY")}` : "",
          },
          {
            icon: Landmark,
            label: "Ledger Balance",
            value: k?.ledger_balance ? inrCompact(k.ledger_balance.value) : "—",
            sub: k?.ledger_balance ? `as of ${dayjs(k.ledger_balance.date).format("DD MMM YYYY")}` : "",
            neg: k?.ledger_balance && k.ledger_balance.value < 0,
          },
          { icon: Percent, label: "GP Margin", value: k?.excl_coins ? `${((k.gp / k.excl_coins) * 100).toFixed(1)}%` : "—", sub: "GP ÷ Turnover excl. Coins" },
          { icon: Percent, label: "GP After Royalty Margin", value: k?.excl_coins ? `${((k.gp_after_royalty / k.excl_coins) * 100).toFixed(1)}%` : "—", sub: "GP after Royalty ÷ Turnover excl. Coins" },
        ].map((m) => (
          <div key={m.label} className="bg-white px-5 py-5">
            <div className="flex items-center gap-2 text-slate-400 mb-2">
              <m.icon size={13} />
              <p className="text-[10px] uppercase tracking-[0.15em] font-semibold">{m.label}</p>
            </div>
            <p className={`font-display text-lg font-semibold tabular-nums ${m.neg ? "text-red-600" : "text-slate-900"}`}>{m.value}</p>
            {m.sub && <p className="text-[11px] text-slate-500 mt-0.5">{m.sub}</p>}
          </div>
        ))}
      </div>

      {insights.length > 0 && (
        <div className="flex flex-wrap gap-2.5 mt-6" data-testid="insights-strip">
          {insights.map((t) => (
            <span
              key={t}
              className="inline-flex items-center gap-1.5 bg-amber-50 border border-amber-200 text-slate-700 text-xs font-semibold px-3.5 py-2 rounded-full"
            >
              <Sparkles size={12} className="text-[#B08D26]" />
              {t}
            </span>
          ))}
        </div>
      )}
    </section>
  );
};
